import { World } from '@ecs/World';
import { Entity } from '@ecs/Entity';
import { Position } from '@components/Position';
import { Velocity } from '@components/Velocity';
import { Renderable, ShapeType } from '@components/Renderable';
import { Lifetime } from '@components/Lifetime';
import { Tag } from '@components/Tag';
import { ParticleDynamics } from '@components/ParticleDynamics';

export interface ExplosionOptions {
  count: number;
  color: string;
  secondaryColor?: string;
  speed?: number;
  size?: number;
}

interface ParticleSpec {
  x: number;
  y: number;
  angle: number;
  speed: number;
  shape: ShapeType;
  color: string;
  size: number;
  lifetime: number;
  drag?: number;
  growth?: number;
}

function spawnParticle(world: World, spec: ParticleSpec): void {
  const entity = new Entity()
    .addComponent(new Position(spec.x, spec.y))
    .addComponent(new Velocity(spec.angle, spec.speed))
    .addComponent(new Renderable(spec.shape, spec.color, spec.size))
    .addComponent(new Lifetime(spec.lifetime, spec.lifetime))
    .addComponent(new Tag('particle'));

  if (spec.drag !== undefined || spec.growth !== undefined) {
    entity.addComponent(new ParticleDynamics(spec.drag ?? 0.95, spec.growth ?? 0));
  }

  world.addEntity(entity);
}

const randomBetween = (min: number, max: number): number => min + Math.random() * (max - min);

/**
 * Particle effect recipes. All effects are plain particle entities, animated by
 * ParticleSystem and drawn by PureRenderSystem.
 */
export const Effects = {
  explosion(world: World, x: number, y: number, options: ExplosionOptions): void {
    const { count, color } = options;
    const secondaryColor = options.secondaryColor ?? '#ffaa00';
    const speed = options.speed ?? 3;
    const size = options.size ?? 3;
    const scale = Math.max(0.5, count / 20);

    // Bright core flash
    spawnParticle(world, {
      x, y, angle: 0, speed: 0, shape: 'particle', color: '#ffffff',
      size: 10 * scale, lifetime: 8, growth: 1.5 * scale
    });

    // Expanding shockwave
    spawnParticle(world, {
      x, y, angle: 0, speed: 0, shape: 'ring', color,
      size: 4, lifetime: 22, drag: 1, growth: 2.2 * scale
    });

    // Fireball in the hull colour
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
      spawnParticle(world, {
        x, y, angle, speed: Math.random() * speed + 1.5, shape: 'particle', color,
        size: size * randomBetween(0.7, 1.3), lifetime: Math.floor(randomBetween(30, 45)), drag: 0.93
      });
    }

    // Hot secondary embers
    for (let i = 0; i < Math.ceil(count / 2); i++) {
      spawnParticle(world, {
        x, y, angle: Math.random() * Math.PI * 2, speed: Math.random() * speed * 0.8 + 0.5,
        shape: 'particle', color: secondaryColor, size: size * 0.8,
        lifetime: Math.floor(randomBetween(25, 40)), drag: 0.92
      });
    }

    // Fast sparks
    Effects.sparks(world, x, y, 0, Math.PI * 2, '#ffe9a8', Math.max(6, Math.ceil(count / 2)), speed * 2.2);

    // Lingering smoke for ship-sized explosions
    if (count >= 20) {
      Effects.smoke(world, x, y, Math.ceil(count / 5), '#555566');
    }
  },

  sparks(world: World, x: number, y: number, angle: number, spread: number, color: string, count: number, speed: number = 5): void {
    for (let i = 0; i < count; i++) {
      spawnParticle(world, {
        x, y, angle: angle + (Math.random() - 0.5) * spread, speed: speed * randomBetween(0.5, 1.2),
        shape: 'spark', color, size: 1.5, lifetime: Math.floor(randomBetween(12, 24)), drag: 0.9
      });
    }
  },

  smoke(world: World, x: number, y: number, count: number, color: string = '#666677'): void {
    for (let i = 0; i < count; i++) {
      spawnParticle(world, {
        x: x + randomBetween(-4, 4), y: y + randomBetween(-4, 4),
        angle: Math.random() * Math.PI * 2, speed: randomBetween(0.2, 0.8),
        shape: 'smoke', color, size: randomBetween(3, 6),
        lifetime: Math.floor(randomBetween(45, 70)), drag: 0.97, growth: 0.25
      });
    }
  },

  muzzleFlash(world: World, x: number, y: number, angle: number, color: string, intensity: number = 1): void {
    spawnParticle(world, {
      x, y, angle, speed: 0.5, shape: 'particle', color: '#ffffff', size: 3 * intensity, lifetime: 4
    });
    Effects.sparks(world, x, y, angle, 0.6, color, Math.ceil(3 * intensity), 4);
  },

  /** Brief ring confirming a move or attack order. */
  orderMarker(world: World, x: number, y: number, color: string): void {
    spawnParticle(world, {
      x, y, angle: 0, speed: 0, shape: 'ring', color, size: 14, lifetime: 14, drag: 1, growth: -0.8
    });
  },

  /** Arrival flash for reinforcements. */
  warpIn(world: World, x: number, y: number, color: string): void {
    spawnParticle(world, { x, y, angle: 0, speed: 0, shape: 'particle', color: '#ffffff', size: 14, lifetime: 10, growth: 1 });
    spawnParticle(world, { x, y, angle: 0, speed: 0, shape: 'ring', color, size: 40, lifetime: 20, drag: 1, growth: -1.8 });
    Effects.sparks(world, x, y, 0, Math.PI * 2, color, 14, 4);
  },

  shieldRipple(world: World, x: number, y: number, angle: number, color: string): void {
    spawnParticle(world, {
      x, y, angle: 0, speed: 0, shape: 'ring', color, size: 10, lifetime: 16, drag: 1, growth: 1.2
    });
    Effects.sparks(world, x + Math.cos(angle) * 14, y + Math.sin(angle) * 14, angle, 1.2, color, 5, 2.5);
  },

  // ---------------------------------------------------------------------------
  // Mothership

  /** Two contracting rings confirming a deployment choice (or recall, or refusal). */
  deployPulse(world: World, x: number, y: number, color: string, radius: number = 26): void {
    spawnParticle(world, { x, y, angle: 0, speed: 0, shape: 'ring', color, size: radius * 1.6, lifetime: 18, drag: 1, growth: -radius * 0.05 });
    spawnParticle(world, { x, y, angle: 0, speed: 0, shape: 'ring', color: '#ffffff', size: radius, lifetime: 12, drag: 1, growth: radius * 0.04 });
    Effects.sparks(world, x, y, 0, Math.PI * 2, color, 8, 2.5);
  },

  /** A burst of welding sparks with a white-hot arc flash. */
  weld(world: World, x: number, y: number, intensity: number = 1): void {
    spawnParticle(world, { x, y, angle: 0, speed: 0, shape: 'particle', color: '#ffffff', size: 3.5 * intensity, lifetime: 5 });
    spawnParticle(world, { x, y, angle: 0, speed: 0, shape: 'particle', color: '#7fd4ff', size: 6 * intensity, lifetime: 4 });
    Effects.sparks(world, x, y, Math.random() * Math.PI * 2, Math.PI * 1.2, '#ffc266', Math.ceil(4 * intensity), 3.2);
  },

  /** Short-lived glowing motes streaming onto a hull being assembled. */
  assemblySparks(world: World, x: number, y: number, color: string): void {
    const angle = Math.random() * Math.PI * 2;
    const distance = randomBetween(18, 34);
    spawnParticle(world, {
      x: x + Math.cos(angle) * distance, y: y + Math.sin(angle) * distance,
      angle: angle + Math.PI, speed: distance / 10, shape: 'particle', color, size: 2, lifetime: 10, drag: 0.98
    });
    Effects.sparks(world, x, y, angle, 0.8, '#ffffff', 1, 2);
  },

  /** Engine wash behind a ship flying out of the hangar. */
  launchTrail(world: World, x: number, y: number, angle: number, color: string): void {
    spawnParticle(world, {
      x, y, angle: angle + Math.PI + (Math.random() - 0.5) * 0.3, speed: randomBetween(1, 2.5),
      shape: 'particle', color, size: randomBetween(2, 3.5), lifetime: Math.floor(randomBetween(14, 22)), drag: 0.92
    });
  },

  /** Hangar doors flaring as the fleet launches. */
  launchFlare(world: World, x: number, y: number, color: string): void {
    spawnParticle(world, { x, y, angle: 0, speed: 0, shape: 'particle', color: '#ffffff', size: 18, lifetime: 12, growth: 1.2 });
    spawnParticle(world, { x, y, angle: 0, speed: 0, shape: 'ring', color, size: 20, lifetime: 30, drag: 1, growth: 3.2 });
    spawnParticle(world, { x, y, angle: 0, speed: 0, shape: 'ring', color: '#ffffff', size: 10, lifetime: 22, drag: 1, growth: 2.2 });
    Effects.sparks(world, x, y, 0, Math.PI * 2, color, 18, 5);
  },

  /** Golden glints around the credits counter as earnings roll in. */
  creditSparkle(world: World, x: number, y: number): void {
    Effects.sparks(world, x, y, -Math.PI / 2, Math.PI * 1.4, '#ffd76a', 3, 2.2);
    spawnParticle(world, { x, y, angle: 0, speed: 0, shape: 'particle', color: '#fff3c4', size: 4, lifetime: 8 });
  },
};
